import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { friendlyError } from '../utils/errorMessages';
import { supabase } from '../lib/supabase';

/**
 * Shared OAuth connect hook — used by Link Accounts page and Onboarding Modal.
 * Returns { connect, disconnect, loading, connections } for LinkedIn and X.
 */
export function useOAuthConnect() {
  const { user, userProfile, updateProfile } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState({});

  const isLinkedInConnected = !!userProfile?.linkedin_access_token;
  const isTwitterConnected = !!userProfile?.twitter_access_token;

  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hash))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const connect = async (provider) => {
    if (!user) { toast.error('Please sign in first.'); return; }
    setLoading(prev => ({ ...prev, [provider]: true }));

    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError || !session?.access_token) {
        toast.error('Session expired. Please sign in again.');
        setLoading(prev => ({ ...prev, [provider]: false }));
        return;
      }

      const clientId = (provider === 'linkedin'
        ? import.meta.env?.VITE_LINKEDIN_CLIENT_ID
        : import.meta.env?.VITE_TWITTER_CLIENT_ID)?.trim();

      if (!clientId || clientId.includes('your-')) {
        toast.warning(`${provider === 'linkedin' ? 'LinkedIn' : 'X'} integration is not configured.`);
        setLoading(prev => ({ ...prev, [provider]: false }));
        return;
      }

      const randomState = Math.random().toString(36).substring(7) + Date.now().toString(36);
      const state = `${provider}:${randomState}`;
      sessionStorage.setItem(`oauth_state_${provider}`, state);
      sessionStorage.setItem(`oauth_user_id_${provider}`, user.id);

      const redirectUri = `${window.location.origin}/oauth-callback`;
      let authUrl;

      if (provider === 'linkedin') {
        authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&scope=${encodeURIComponent('openid profile email w_member_social')}`;
      } else {
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        sessionStorage.setItem(`oauth_code_verifier_${provider}`, codeVerifier);
        authUrl = `https://x.com/i/oauth2/authorize?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&scope=${encodeURIComponent('tweet.write tweet.read users.read offline.access')}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
      }

      const width = 600, height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const popup = window.open(authUrl, `${provider}_oauth`, `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`);

      if (!popup) {
        toast.error('Pop-up blocked. Please allow pop-ups.');
        setLoading(prev => ({ ...prev, [provider]: false }));
        return;
      }

      let checkClosed = null;
      let messageReceived = false;

      const handleMessage = async (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'oauth_success' && event.data?.provider === provider) {
          messageReceived = true;
          if (checkClosed) clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          if (bc) { bc.close(); bc = null; }

          try {
            const { data: { session: fresh } } = await supabase.auth.getSession();
            if (!fresh?.access_token) throw new Error('Session expired');

            const codeVerifier = sessionStorage.getItem(`oauth_code_verifier_${provider}`);
            const response = await fetch(
              `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/oauth-exchange`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${fresh.access_token}` },
                body: JSON.stringify({ code: event.data.code, provider, redirectUri, codeVerifier })
              }
            );
            const result = await response.json();
            if (!result.success) throw new Error(result.error || 'Token exchange failed');

            toast.success(`${provider === 'linkedin' ? 'LinkedIn' : 'X'} connected!`);
            await updateProfile({});
            sessionStorage.removeItem(`oauth_state_${provider}`);
            sessionStorage.removeItem(`oauth_code_verifier_${provider}`);
            sessionStorage.removeItem(`oauth_user_id_${provider}`);
          } catch (error) {
            toast.error(friendlyError(error));
          } finally {
            setLoading(prev => ({ ...prev, [provider]: false }));
          }
        } else if (event.data?.type === 'oauth_error') {
          messageReceived = true;
          if (checkClosed) clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          if (bc) { bc.close(); bc = null; }
          toast.error(friendlyError(event.data?.errorDescription || event.data?.error));
          setLoading(prev => ({ ...prev, [provider]: false }));
        }
      };

      window.addEventListener('message', handleMessage);

      // BroadcastChannel fallback — window.opener can be null after cross-origin OAuth redirects
      let bc = null;
      try {
        bc = new BroadcastChannel('oauth_channel');
        bc.onmessage = (e) => handleMessage({ origin: window.location.origin, data: e.data });
      } catch (_) {}

      checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          if (!messageReceived) {
            window.removeEventListener('message', handleMessage);
            if (bc) { bc.close(); bc = null; }
            setLoading(prev => ({ ...prev, [provider]: false }));
          }
        }
      }, 1000);
    } catch (error) {
      toast.error(friendlyError(error));
      setLoading(prev => ({ ...prev, [provider]: false }));
    }
  };

  return { connect, loading, isLinkedInConnected, isTwitterConnected };
}
